import { formatShortDate } from "./api";

export const RESULTS_HISTORY_LIMIT = 200;

export function resultDateKey(r) {
  const raw = r?.fecha || r?.draw_date || r?.date || "";
  const s = String(raw);
  const iso = s.match(/(\d{4}-\d{2}-\d{2})/);
  if (iso) return iso[1];
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

export function resultMatchesLottery(r, lotteryId, lotteries = []) {
  if (!lotteryId) return true;
  if (r?.lottery_id && String(r.lottery_id) === String(lotteryId)) return true;
  const lottery = lotteries.find((l) => String(l.id) === String(lotteryId));
  if (!lottery?.name || !r?.lottery_name) return false;
  return String(r.lottery_name).toLowerCase().includes(String(lottery.name).toLowerCase());
}

export function lotteriesFromResults(results = [], catalog = []) {
  const byId = new Map();
  catalog.forEach((lottery) => {
    if (lottery?.id) byId.set(String(lottery.id), lottery);
  });
  results.forEach((r) => {
    const name = r?.lottery_name;
    if (!name) return;
    const id = r.lottery_id ? String(r.lottery_id) : name;
    if (!byId.has(id) && ![...byId.values()].some((l) => l.name === name)) {
      byId.set(id, { id, name });
    }
  });
  return Array.from(byId.values()).sort((a, b) =>
    String(a.name).localeCompare(String(b.name), "es")
  );
}

export function formatResultDateLabel(dateKey) {
  if (!dateKey) return "";
  return formatShortDate(/T/.test(dateKey) ? dateKey : `${dateKey}T12:00:00`);
}

export function uniqueResultDates(results = [], lotteryId = "", lotteries = []) {
  const keys = new Set();
  results.forEach((r) => {
    if (!resultMatchesLottery(r, lotteryId, lotteries)) return;
    const key = resultDateKey(r);
    if (key) keys.add(key);
  });
  return Array.from(keys).sort().reverse();
}

export function filterLotteryResults(
  results = [],
  { lotteryId = "", date = "", numero = "", serie = "" } = {},
  lotteries = []
) {
  const n = String(numero || "").replace(/\D/g, "");
  const s = String(serie || "").replace(/\D/g, "");
  return results.filter((r) => {
    if (!resultMatchesLottery(r, lotteryId, lotteries)) return false;
    if (date && resultDateKey(r) !== date) return false;
    if (n) {
      const rn = String(r.numero || "").replace(/\D/g, "").padStart(4, "0").slice(-4);
      if (rn !== n.padStart(4, "0").slice(-4)) return false;
    }
    if (s) {
      const rs = String(r.numero_serie || "").replace(/\D/g, "").padStart(3, "0").slice(-3);
      if (rs !== s.padStart(3, "0").slice(-3)) return false;
    }
    return true;
  });
}

export function applyResultsSearch(rawResults, payload, lotteries = []) {
  const lotteryId = payload?.lotteryId || "";
  const date = payload?.date || "";
  const numero = payload?.numero || "";
  const serie = payload?.serie || "";
  const hasNumber = String(numero).replace(/\D/g, "").length >= 4;
  const hasDate = Boolean(date);

  if (!hasDate && !hasNumber) {
    return {
      ok: false,
      error: "Selecciona una fecha de sorteo.",
      matches: [],
    };
  }

  const matches = filterLotteryResults(
    rawResults,
    { lotteryId, date, numero, serie },
    lotteries
  );

  if (matches.length === 0) {
    return {
      ok: false,
      error: "No aparecen sorteos para esa consulta. Prueba otra lotería o fecha.",
      matches: [],
    };
  }

  return {
    ok: true,
    matches,
    message: hasNumber
      ? `Encontramos ${matches.length} sorteo${matches.length === 1 ? "" : "s"} con esa combinación.`
      : `Mostrando ${matches.length} sorteo${matches.length === 1 ? "" : "s"} de la consulta.`,
  };
}

function simpleHashColor(str) {
  if (!str) return "#b18635";
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = str.charCodeAt(i) + ((h << 5) - h);
  }
  const hue = Math.abs(h) % 360;
  return `hsl(${hue} 42% 36%)`;
}

/**
 * Adapta LotteryResult (API) al shape esperado por ResultsGrid / ResultCard.
 * @param {object} r
 * @param {number} index
 */
export function mapLotteryResultToGridItem(r, index = 0) {
  const rawNum = (r.numero ?? "").toString().replace(/\D/g, "");
  const padded = rawNum.padStart(4, "0").slice(-4);
  const numberDigits = padded.split("");

  const rawSerie = (r.numero_serie ?? "").toString().replace(/\D/g, "");
  const seriePadded = rawSerie.padStart(3, "0").slice(-3);
  const seriesDigits = seriePadded.split("");

  const fechaLabel = formatResultDateLabel(resultDateKey(r));

  return {
    id: r.id || `r-${index}`,
    lotteryName: r.lottery_name || "Lotería",
    date: fechaLabel,
    numberDigits,
    seriesDigits,
    color: simpleHashColor(r.lottery_name),
    logoUrl: r.logo_url || r.lottery_logo || null,
    raw: r,
  };
}
